import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';

export const QUARTER_HOUR_ROW_HEIGHT = 28;
const VISIBLE_ROWS = 9;
const PREFERRED_START_MINUTES = 8 * 60;
const PREFERRED_END_MINUTES = 18 * 60 + 45;
const MENU_GAP_PX = 2;

function minutesOfDay(value) {
  return value.hours() * 60 + value.minutes();
}

function isQuarterHour(value) {
  return (
    value.seconds() === 0 &&
    value.milliseconds() === 0 &&
    value.minutes() % 15 === 0
  );
}

function formatMinutes(minutes) {
  return moment()
    .startOf('day')
    .add(minutes, 'minutes')
    .format('h:mm A');
}

function momentFromMinutes(minutes, base) {
  const next = moment.isMoment(base) ? base.clone() : moment();
  const hours = Math.floor(minutes / 60);
  next.hours(hours);
  next.minutes(minutes - hours * 60);
  next.seconds(0);
  next.milliseconds(0);
  return next;
}

function buildTimeOptions(selected) {
  const options = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    options.push({
      minutes,
      label: formatMinutes(minutes),
    });
  }

  if (selected && moment.isMoment(selected) && selected.isValid() && !isQuarterHour(selected)) {
    const extraMinutes = minutesOfDay(selected);
    const extra = {
      minutes: extraMinutes,
      label: selected.format('h:mm A'),
    };
    const insertAt = options.findIndex((option) => option.minutes > extraMinutes);
    if (insertAt === -1) {
      options.push(extra);
    } else {
      options.splice(insertAt, 0, extra);
    }
  }

  return options;
}

function getSelectedIndex(options, selected) {
  if (!selected || !moment.isMoment(selected) || !selected.isValid()) {
    return -1;
  }
  const selectedMinutes = minutesOfDay(selected);
  return options.findIndex((option) => option.minutes === selectedMinutes);
}

function getViewportStartIndex(options, selectedIndex) {
  const maxStart = Math.max(0, options.length - VISIBLE_ROWS);
  if (selectedIndex < 0) {
    const preferredStart = options.findIndex(
      (option) => option.minutes >= PREFERRED_START_MINUTES,
    );
    return preferredStart < 0 ? 0 : Math.min(preferredStart, maxStart);
  }

  const selectedMinutes = options[selectedIndex].minutes;
  const inPreferredRange =
    selectedMinutes >= PREFERRED_START_MINUTES &&
    selectedMinutes <= PREFERRED_END_MINUTES;

  if (!inPreferredRange) {
    return Math.min(Math.max(selectedIndex - 4, 0), maxStart);
  }

  const preferredStartIndex = options.findIndex(
    (option) => option.minutes >= PREFERRED_START_MINUTES,
  );
  let preferredEndIndex = -1;
  for (let i = options.length - 1; i >= 0; i -= 1) {
    if (options[i].minutes <= PREFERRED_END_MINUTES) {
      preferredEndIndex = i;
      break;
    }
  }

  const rangeStart = preferredStartIndex < 0 ? 0 : preferredStartIndex;
  const rangeEnd = preferredEndIndex < 0 ? options.length - 1 : preferredEndIndex;
  const maxPreferredStart = Math.max(rangeStart, rangeEnd - VISIBLE_ROWS + 1);
  return Math.min(Math.max(selectedIndex - 4, rangeStart), maxPreferredStart);
}

export default function QuarterHourTimeSelect({
  value,
  onChange,
  disabled = false,
  name = 'time',
  placeholder = 'HH:MM AM',
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0 });
  const controlRef = useRef(null);
  const listRef = useRef(null);

  const options = useMemo(() => buildTimeOptions(value), [value]);
  const selectedIndex = getSelectedIndex(options, value);
  const selectedLabel =
    selectedIndex >= 0 ? options[selectedIndex].label : '';

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) {
      return;
    }
    const rect = controlRef.current
      ? controlRef.current.getBoundingClientRect()
      : null;
    if (rect) {
      setMenuPos({
        top: Math.round(rect.bottom + MENU_GAP_PX),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      });
    }
    setOpen(true);
  }, [disabled]);

  const handleToggle = useCallback(() => {
    if (open) {
      closeMenu();
      return;
    }
    openMenu();
  }, [closeMenu, open, openMenu]);

  const handleSelect = useCallback(
    (minutes) => {
      onChange?.(momentFromMinutes(minutes, value));
      closeMenu();
    },
    [closeMenu, onChange, value],
  );

  useLayoutEffect(() => {
    if (!open || !listRef.current) {
      return;
    }
    const startIndex = getViewportStartIndex(options, selectedIndex);
    listRef.current.scrollTop = startIndex * QUARTER_HOUR_ROW_HEIGHT;
  }, [open, options, selectedIndex]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      const control = controlRef.current;
      const list = listRef.current;
      if (control && control.contains(event.target)) {
        return;
      }
      if (list && list.contains(event.target)) {
        return;
      }
      closeMenu();
    };

    const onKeyDown = (event) => {
      if (event.key !== 'Escape' && event.keyCode !== 27) {
        return;
      }
      event.preventDefault();
      closeMenu();
    };

    const onReposition = () => {
      const rect = controlRef.current
        ? controlRef.current.getBoundingClientRect()
        : null;
      if (!rect) {
        return;
      }
      setMenuPos({
        top: Math.round(rect.bottom + MENU_GAP_PX),
        left: Math.round(rect.left),
        width: Math.round(rect.width),
      });
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [closeMenu, open]);

  return (
    <div className="quarter-hour-time-select">
      <button
        ref={controlRef}
        type="button"
        name={name}
        className="quarter-hour-time-select__control"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={handleToggle}
      >
        {selectedLabel || placeholder}
      </button>
      {open
        ? createPortal(
            <div
              className="quarter-hour-time-select__menu"
              style={{
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
              }}
            >
              <div
                ref={listRef}
                className="quarter-hour-time-select__list"
                role="listbox"
              >
                {options.map((option, index) => (
                  <button
                    type="button"
                    key={`${option.minutes}-${option.label}`}
                    className={
                      'quarter-hour-time-select__option' +
                      (index === selectedIndex
                        ? ' quarter-hour-time-select__option--selected'
                        : '')
                    }
                    role="option"
                    aria-selected={index === selectedIndex}
                    onClick={() => handleSelect(option.minutes)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
